import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  HttpError,
  useCreate,
  useGetIdentity,
  useOne,
  useParse,
  useTranslation,
  useUpdate,
} from "@refinedev/core";
import {
  CreateEventStep,
  FrequencyOfSellType,
  IAdditionalSetupData,
  IAttractionAddtionalSetupForm,
  ISimpleEventDetail,
  IUserData,
  PrivateFlowVerifyType,
  QRCodeType,
  SupportedCurrency,
  SupportedLanguage,
  UserType,
} from "interfaces";
import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { BasicInformation } from "./basicInformation";
import Button from "components/button";
import { Link, useNavigate } from "react-router-dom";
import { PaymentMethod } from "./paymentMethod";
import { SwitchButton } from "components/switchButton";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";
import { get, isEmpty } from "lodash";
import { languageSchema } from "routes/event/schema";
import {
  DEFAULT_PAYMENT_METHOD_VALUE,
  DEFAULT_USD_CURRENCY_PAYMENT_METHODS_VALUE,
} from "utills/constants";
import { PrivateFlow } from "./privateFlow";
import { canAccessByType } from "utills/helpers";
import { LegalInfo } from "./legalInfo";
import i18n from "utills/i18n";
import { Einvoice } from "./einvoice";
import { Label } from "components/label";

enum Section {
  BASIC_INFOMATION = "basic_information",
  PAYMENT_METHOD = "payment_method",
  PRIVATE_FLOW = "private_flow",
  LEGAL_INFO = "legal_info",
  EINVOICE = "einvoice",
  NONE = "none",
}

const AdditionalSetupSchema = (
  languages: SupportedLanguage[],
  userType?: UserType
) => {
  return z.object({
    basic_information: z
      .object({
        is_hide_vat: z.boolean(),
        is_send_qr_code: z.boolean(),
        is_ignore_participant_step: z.boolean(),
        seat_map_url: z.string().optional(),
        special_template: z.string().optional(),
        qr_code_type: z.string().nullable(),
        event_term_url: languageSchema(languages),
        hold_ticket_time: z
          .number({
            invalid_type_error: "Hold ticket time must be a number",
            required_error: "Hold ticket time is required",
          })
          .min(0, {
            message: "Hold ticket time must be equal or greater than 0",
          })
          .nonnegative({
            message: "Hold ticket time must be a positive number",
          }),
        max_tickets_to_buy: z
          .union([
            z
              .number()
              .int()
              .min(
                0,
                "Max tickets quantity per account must be equal or greater than 0"
              )
              .nullable(),
            z.nan(),
          ])
          .optional(),
        order_min_qty_ticket: z
          .number({
            invalid_type_error:
              "Min tickets quantity per order must be a number",
            required_error: "Min tickets quantity per order is required",
          })
          .min(1, {
            message:
              "Min tickets quantity per order must be equal or greater than 1",
          })
          .nonnegative({
            message: "Min tickets quantity per order must be a positive number",
          }),
        order_max_qty_ticket: z
          .number({
            invalid_type_error:
              "Max tickets quantity per order must be a number",
            required_error: "Max tickets quantity per order is required",
          })
          .min(1, {
            message:
              "Max tickets quantity per order must be equal or greater than 1",
          })
          .nonnegative({
            message: "Max tickets quantity per order must be a positive number",
          }),
        note_individual_ticket_purchase_flow: languageSchema(languages, true),
        ...(userType === UserType.ADMIN && {
          use_pass_tool: z.boolean(),
          use_authentication: z.boolean(),
          is_static_html: z.boolean(),
          static_html_content: z.string().optional().nullable(),
          is_display_on_the_organization_detail_page: z.boolean(),
        }),
      })
      .refine(
        (data) => data.order_min_qty_ticket <= data.order_max_qty_ticket,
        {
          message:
            "The value must be equal or greater than the Min value field",
          path: ["order_max_qty_ticket"],
        }
      ),
    private_flow: z
      .object({
        is_use: z.boolean(),
        verify_type: z
          .string()
          .nullable()
          .transform((value) => (value === "" ? null : value)),
        usage_count: z.preprocess(
          (val) => {
            if (val === "" || val === undefined || val === null) {
              return null;
            }
            return Number(val);
          },
          z
            .number({
              invalid_type_error: "Please input a number",
            })
            .nullable()
            .optional()
        ),
        sync_enabled: z.boolean(),
        passcode_source_event_ids: z.array(z.string()).optional(),
        email_domain: z.string().transform((value) => {
          return value
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line !== "")
            .join("\n");
        }),
      })
      .superRefine((value, ctx) => {
        if (value.is_use) {
          if (isEmpty(value.verify_type)) {
            ctx.addIssue({
              path: ["verify_type"],
              code: z.ZodIssueCode.custom,
              message: i18n.t("validation.errorMessage.thisFieldIsRequired"),
            });
          }
          if (value.verify_type === PrivateFlowVerifyType.DOMAIN_EMAIL) {
            const lines = (value.email_domain || "").split("\n");

            const domainRegex = /^@([\w-]+\.)+[a-zA-Z]{2,}$/;
            const invalidDomains = [];

            for (let i = 0; i < lines.length; i++) {
              const domain = lines[i].trim();
              if (!domainRegex.test(domain)) {
                invalidDomains.push(`"${domain}" (line ${i + 1})`);
              }
            }
            if (invalidDomains.length > 0) {
              ctx.addIssue({
                path: ["email_domain"],
                code: z.ZodIssueCode.custom,
                message: `Invalid domain: ${invalidDomains.join(", ")}.`,
              });
            }
          }
          if (value.sync_enabled) {
            if (isEmpty(value.passcode_source_event_ids)) {
              ctx.addIssue({
                path: ["passcode_source_event_ids"],
                code: z.ZodIssueCode.custom,
                message: i18n.t("validation.errorMessage.thisFieldIsRequired"),
              });
            }
            if (
              typeof value.usage_count !== "number" ||
              isNaN(value.usage_count)
            ) {
              ctx.addIssue({
                path: ["usage_count"],
                code: z.ZodIssueCode.custom,
                message: i18n.t("validation.errorMessage.mustBeANumber"),
              });
              return;
            }
            if (value.usage_count < 0) {
              ctx.addIssue({
                path: ["usage_count"],
                code: z.ZodIssueCode.custom,
                message: i18n.t("validation.errorMessage.minNum", { min: 0 }),
              });
            }
          }
        }
      }),
    payment_method: z.object({
      ignore_payment_methods: z
        .array(z.string())
        .min(1, "This field is required"),
      payment_gateway_promotion_id: z.string().optional(),
      ...(userType === UserType.ADMIN && {
        order_code_prefix: z
          .string()
          .trim()
          .max(10, {
            message: "Maximum 10 characters",
          })
          .regex(/^[a-zA-Z0-9]*$/, {
            message: "Please do not enter special character",
          })
          .optional()
          .nullable(),
      })
    }),
    legal_info: z.object({
      company_name: z.string().optional().or(z.literal("")),
      address: z.string().optional().or(z.literal("")),
      tax_code: z.string().optional().or(z.literal("")),
      phone: z.string().optional().or(z.literal("")),
      representative: z.string().optional().or(z.literal("")),
      position: z.string().optional().or(z.literal("")),
      account_name: z.string().optional().or(z.literal("")),
      account_number: z.string().optional().or(z.literal("")),
      bank: z.string().optional().or(z.literal("")),
      contract_number: z.string().optional().or(z.literal("")),
      service_fee: z
        .union([
          z.preprocess(
            (val) => (val === "" || val === null ? null : Number(val)),
            z
              .number()
              .min(0, "Value must be between 0 and 100")
              .max(100, "Value must be between 0 and 100")
              .nullable()
          ),
          z.nan(),
        ])
        .optional(),
      vat: z
        .union([
          z.preprocess(
            (val) => (val === "" || val === null ? null : Number(val)),
            z
              .number()
              .min(0, "Value must be between 0 and 100")
              .max(100, "Value must be between 0 and 100")
              .nullable()
          ),
          z.nan(),
        ])
        .optional(),
    }),
    einvoice: z
      .object({
        is_einvoice_enabled: z.boolean(),
        label_event_on_einvoice: z
          .string()
          .nullable()
          .transform((value) => (value === "" ? null : value)),
      })
      .superRefine((value, ctx) => {
        if (value.is_einvoice_enabled) {
          if (isEmpty(value.label_event_on_einvoice)) {
            ctx.addIssue({
              path: ["label_event_on_einvoice"],
              code: z.ZodIssueCode.custom,
              message: i18n.t("validation.errorMessage.thisFieldIsRequired"),
            });
          }
        }
      }),
  });
};

export const AdditionalSetup = (props: {
  eventDetailData: ISimpleEventDetail;
  refetchEventDetail: () => void;
}) => {
  const parse = useParse();
  const { params } = parse();

  const {
    data,
    isFetching,
    refetch: refetchAdditionalSetup,
  } = useOne<IAdditionalSetupData>({
    resource: "content/events/attractions",
    id: `${params?.id}/additional`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(
        params?.id &&
        params?.step &&
        get(props.eventDetailData, "is_addition_done", false)
      ),
    } as any,
  });

  const defaultAddtionalData = data?.data ?? null;

  return !isFetching ? (
    <AdditionalSetupForm
      {...props}
      defaultAddtionalData={defaultAddtionalData ?? null}
      refetchAdditionalSetup={refetchAdditionalSetup}
      refetchEventDetail={props.refetchEventDetail}
    />
  ) : (
    <></>
  );
};

const AdditionalSetupForm = ({
  eventDetailData,
  defaultAddtionalData,
  refetchAdditionalSetup,
  refetchEventDetail,
}: {
  eventDetailData: ISimpleEventDetail;
  defaultAddtionalData: IAdditionalSetupData | null;
  refetchAdditionalSetup: () => void;
  refetchEventDetail: () => void;
}) => {
  const [openSectionId, setOpenSectionId] = useState<Section>(Section.NONE);
  const parse = useParse();
  const { params } = parse();
  const { translate } = useTranslation();

  const { mutate, isLoading: isLoading } = useCreate();
  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();

  const navigate = useNavigate();
  const { data: identity } = useGetIdentity<IUserData>();

  const methods = useForm<
    IAttractionAddtionalSetupForm,
    HttpError,
    IAttractionAddtionalSetupForm
  >({
    defaultValues: {
      basic_information: {
        event_term_url: defaultAddtionalData?.event_term_url ?? {
          en: "",
          vi: "",
        },
        hold_ticket_time: Math.floor(
          (defaultAddtionalData?.hold_ticket_time ?? 1800) / 60
        ), // seconds to minutes
        is_hide_vat: defaultAddtionalData?.is_hide_vat ?? false,
        max_tickets_to_buy: defaultAddtionalData?.max_tickets_to_buy ?? null,
        order_min_qty_ticket: defaultAddtionalData?.order_min_qty_ticket ?? 1,
        order_max_qty_ticket: defaultAddtionalData?.order_max_qty_ticket ?? 50,
        note_individual_ticket_purchase_flow:
          defaultAddtionalData?.note_individual_ticket_purchase_flow ?? {
            en: "",
            vi: "",
          },
        is_ignore_participant_step:
          defaultAddtionalData?.is_ignore_participant_step ?? false,
        is_send_qr_code: defaultAddtionalData?.is_send_qr_code ?? false,
        seat_map_url: defaultAddtionalData?.seat_map_url ?? "",
        special_template: defaultAddtionalData?.special_template ?? "",
        qr_code_type: defaultAddtionalData?.qr_code_type ?? QRCodeType.GENERATE,
        use_pass_tool: defaultAddtionalData?.use_pass_tool ?? false,
        use_authentication: defaultAddtionalData?.use_authentication ?? true,
        is_static_html: defaultAddtionalData?.is_static_html ?? false,
        static_html_content: defaultAddtionalData?.static_html_content ?? "",
        is_display_on_the_organization_detail_page: defaultAddtionalData?.is_display_on_the_organization_detail_page ?? false,
      },
      payment_method: {
        ignore_payment_methods: defaultAddtionalData?.ignore_payment_methods
          ? defaultAddtionalData?.ignore_payment_methods ?? []
          : eventDetailData?.current_step < 4
          ? eventDetailData?.currency === SupportedCurrency.VND
            ? DEFAULT_PAYMENT_METHOD_VALUE
            : DEFAULT_USD_CURRENCY_PAYMENT_METHODS_VALUE
          : [],
        payment_gateway_promotion_id:
          defaultAddtionalData?.payment_gateway_promotion_id ?? "",
        order_code_prefix: defaultAddtionalData?.order_code_prefix ?? ""
      },
      private_flow: {
        email_domain: defaultAddtionalData?.private_flow?.email_domain
          ? defaultAddtionalData?.private_flow?.email_domain.replace(",", "\n")
          : "",
        is_use: defaultAddtionalData?.private_flow?.is_use ?? false,
        verify_type: defaultAddtionalData?.private_flow?.verify_type ?? "",
        usage_count: defaultAddtionalData?.private_flow?.usage_count ?? 1,
        sync_enabled: defaultAddtionalData?.private_flow?.sync_enabled ?? false,
        passcode_source_event_ids:
          defaultAddtionalData?.private_flow?.passcode_source_event_ids ?? [],
      },
      legal_info: {
        company_name: defaultAddtionalData?.legal_info?.company_name ?? "",
        address: defaultAddtionalData?.legal_info?.address ?? "",
        tax_code: defaultAddtionalData?.legal_info?.tax_code ?? "",
        phone: defaultAddtionalData?.legal_info?.phone ?? "",
        representative: defaultAddtionalData?.legal_info?.representative ?? "",
        position: defaultAddtionalData?.legal_info?.position ?? "",
        account_name: defaultAddtionalData?.legal_info?.account_name ?? "",
        account_number: defaultAddtionalData?.legal_info?.account_number ?? "",
        bank: defaultAddtionalData?.legal_info?.bank ?? "",
        contract_number:
          defaultAddtionalData?.legal_info?.contract_number ?? "",
        service_fee: defaultAddtionalData?.legal_info?.service_fee ?? "",
        vat: defaultAddtionalData?.legal_info?.vat ?? "",
      },
      einvoice: {
        is_einvoice_enabled: defaultAddtionalData?.is_einvoice_enabled ?? false,
        label_event_on_einvoice:
          defaultAddtionalData?.label_event_on_einvoice ?? "",
      },
    },
    mode: "onChange",
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        AdditionalSetupSchema(eventDetailData.languages, identity?.type)
      );
      return createResolver(values, context, options);
    },
  });

  const onSubmit = (data: IAttractionAddtionalSetupForm) => {
    data.basic_information.hold_ticket_time = Math.floor(
      data.basic_information.hold_ticket_time * 60
    ); // minutes to seconds
    data.private_flow.email_domain = data.private_flow.email_domain
      .replace("\n", ",")
      .trim();

    const submitValues = {
      // event_id: eventDetailData.id,
      languages: eventDetailData.languages,
      info: {
        ...data.basic_information,
        ...data.payment_method,
        ...data.einvoice,
        private_flow: {
          ...data.private_flow,
        },
      },
    };

    const isCreate = !get(eventDetailData, "is_addition_done", false);

    if (isCreate) {
      _createAdditionalSetup(submitValues);
    } else {
      _updateAdditionalSetup(submitValues);
    }
  };

  const _createAdditionalSetup = (submitValues: any) => {
    mutate(
      {
        resource: `content/events/attractions/${eventDetailData.id}/additional`,
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: (data) => {
          toast.success("Create successfully");
          refetchAdditionalSetup();
          navigate(`/events/attractions`);
        },
      }
    );
  };

  const _updateAdditionalSetup = (submitValues: any) => {
    updateMutation(
      {
        resource: "content/events/attractions",
        id: `${eventDetailData.id}/additional`,
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: (data) => {
          toast.success("Update successfully");
          refetchEventDetail();
          // refetchAdditionalSetup();
        },
      }
    );
  };

  const onError = (errors: any) => {
    methods.trigger(openSectionId, { shouldFocus: true }).then((isValid) => {
      if (isValid) {
        const firstError = getFirstError(
          errors
        ) as keyof IAttractionAddtionalSetupForm;
        if (firstError) {
          methods.trigger(firstError).then(() => {
            setOpenSectionId(firstError as Section);
          });
        }
      }
    });
  };

  const getFirstError = (errors: any) => {
    const firstError = (
      Object.keys(errors) as Array<keyof typeof errors>
    ).reduce<keyof typeof errors | null>((field, a) => {
      const fieldKey = field as keyof typeof errors;
      return !!errors[fieldKey] ? fieldKey : a;
    }, null);
    return firstError;
  };

  const _handleSubmitSection = (sectionId: Section) => {
    if (!openSectionId) return setOpenSectionId(sectionId);
    methods.trigger(openSectionId).then((isValid) => {
      if (isValid)
        setOpenSectionId(
          openSectionId === sectionId ? Section.NONE : sectionId
        );
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit, onError)}
        className="h-full"
      >
        <div className="flex flex-col gap-y-4 h-full sm:gap-y-6">
          <div className="box-container p-0">
            <div className="flex flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 flex-row sm:items-center sm:justify-between">
              <h3 className="flex-1 shrink-0 text-base font-semibold leading-6">
                Basic information
              </h3>
              <button
                onClick={() => _handleSubmitSection(Section.BASIC_INFOMATION)}
                type="button"
              >
                {openSectionId === Section.BASIC_INFOMATION ? (
                  <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                ) : (
                  <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                )}
              </button>
            </div>
            <BasicInformation
              isEdit={openSectionId === Section.BASIC_INFOMATION}
              eventDetailData={eventDetailData}
            />
          </div>
          {get(eventDetailData, "frequency_of_sell", "") ===
          FrequencyOfSellType.ONCE ? (
            <div className="box-container p-0">
              <div className="flex flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 flex-row sm:items-center sm:justify-between">
                <div className="flex-1 shrink-0">
                  <h3 className="text-base font-semibold leading-6">
                    Private flow
                  </h3>
                  <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
                    Use Priavte flow
                    <Controller
                      control={methods.control}
                      name="private_flow.is_use"
                      render={({ field: { onChange, value, ref } }) => (
                        <SwitchButton
                          checked={value}
                          onChange={(isCheck) => {
                            onChange(isCheck);
                            if (
                              !isCheck &&
                              openSectionId === Section.PRIVATE_FLOW
                            ) {
                              setOpenSectionId(Section.NONE);
                            }
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
                {methods.watch("private_flow.is_use") ? (
                  <button
                    onClick={() => _handleSubmitSection(Section.PRIVATE_FLOW)}
                    type="button"
                  >
                    {openSectionId === Section.PRIVATE_FLOW ? (
                      <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                    ) : (
                      <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                    )}
                  </button>
                ) : (
                  <></>
                )}
              </div>
              <PrivateFlow
                isEdit={openSectionId === Section.PRIVATE_FLOW}
                eventDetailData={eventDetailData}
              />
            </div>
          ) : (
            <></>
          )}
          <div className="box-container p-0">
            <div className="flex flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 flex-row sm:items-center sm:justify-between">
              <h3 className="flex-1 shrink-0 text-base font-semibold leading-6">
                Payment Method
              </h3>
              <button
                onClick={() => _handleSubmitSection(Section.PAYMENT_METHOD)}
                type="button"
              >
                {openSectionId === Section.PAYMENT_METHOD ? (
                  <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                ) : (
                  <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                )}
              </button>
            </div>
            <PaymentMethod
              isEdit={openSectionId === Section.PAYMENT_METHOD}
              eventDetailData={eventDetailData}
            />
          </div>
          <div className="box-container p-0">
            <div className="flex flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 flex-row sm:items-center sm:justify-between">
              <div className="flex-1 shrink-0">
                <h3 className="text-base font-semibold leading-6">
                  {translate("eventSetup.additionalSetup.section.einvoice")}
                </h3>
                <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
                  <Label
                    id="is_change_registration"
                    className="m-0 font-normal"
                    name={translate(
                      "eventSetup.additionalSetup.einvoice.isEinvoiceEnabled",
                    )}
                  />
                  <Controller
                    control={methods.control}
                    name="einvoice.is_einvoice_enabled"
                    render={({ field: { onChange, value, ref } }) => (
                      <SwitchButton
                        checked={value}
                        onChange={(isCheck) => {
                          onChange(isCheck);
                          if (!isCheck) {
                            if (openSectionId === Section.EINVOICE) {
                              setOpenSectionId(Section.NONE);
                            }
                            methods.setValue("einvoice.label_event_on_einvoice", "");
                          }
                        }}
                      />
                    )}
                  />
                </div>
              </div>
              {methods.watch("einvoice.is_einvoice_enabled") ? (
                <button
                  onClick={() => _handleSubmitSection(Section.EINVOICE)}
                  type="button"
                >
                  {openSectionId === Section.EINVOICE ? (
                    <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                  ) : (
                    <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                  )}
                </button>
              ) : (
                <></>
              )}
            </div>
            <Einvoice
              isEdit={openSectionId === Section.EINVOICE}
              eventDetailData={eventDetailData}
            />
          </div>

          {canAccessByType(UserType.ADMIN) && (
            <div className="box-container p-0">
              <div className="flex flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 flex-row sm:items-center sm:justify-between">
                <h3 className="flex-1 shrink-0 text-base font-semibold leading-6">
                  Legal Info
                </h3>
                <button
                  onClick={() => _handleSubmitSection(Section.LEGAL_INFO)}
                  type="button"
                >
                  {openSectionId === Section.LEGAL_INFO ? (
                    <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                  ) : (
                    <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                  )}
                </button>
              </div>
              <LegalInfo
                isEdit={openSectionId === Section.LEGAL_INFO}
                eventDetailData={eventDetailData}
              />
            </div>
          )}
          <div
            className="mt-auto w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2]"
            style={{ boxShadow: "rgba(0, 0, 0, 0.23) 0px -5px 5px -5px" }}
          >
            <div className="flex items-center justify-end gap-x-4">
              <Link
                to={`/events/attractions/${params?.id}/edit?step=${CreateEventStep.CREATE_TICKET_PHASE}`}
              >
                <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                  Back
                </Button>
              </Link>
              <Button className="w-fit min-w-20" type="submit">
                {eventDetailData.current_step >= params?.step
                  ? "Save"
                  : "Save & Next"}
              </Button>
            </div>
          </div>
        </div>
      </form>
      {isLoading || updateMutationIsLoading ? <FullScreenLoading /> : <></>}
    </FormProvider>
  );
};
